import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Volume2, PenLine, Eye, Sparkles } from "lucide-react";
import { StudentShell } from "../components/StudentShell";
import { api, useAuth } from "../api";
import { gradeProfile } from "../gradeProfile";
import { sfx } from "../sfx";
import { say } from "../components/tasks";

const LETTERS = "abcdefghijklmnopqrstuvwxyz".split("");

const PROMPTS = {
  3: ["Write your name in English.", "Write two words about your school.",
      "Write the colour you like: I like ___."],
  4: ["Write two short sentences about your family.", "Write three things in your school bag.",
      "Write one sentence about your favourite food."],
  5: ["Write three sentences about your day.", "Write about your best friend (3 sentences).",
      "Write four sentences about your home."],
  6: ["Write five sentences about your house.", "Write five sentences about a trip you liked.",
      "Write a short paragraph about your hobby."],
  7: ["Write five sentences about your dream job.", "Write a short paragraph about your city.",
      "Write five sentences about a person you admire."],
  8: ["Write a paragraph (6-8 sentences) about technology in your life.",
      "Write a paragraph about a problem in your town and one solution.",
      "Write a paragraph about your plans after school."],
  9: ["Write a short essay (8-10 sentences) about the importance of English.",
      "Write an opinion paragraph: should students study online? Give reasons.",
      "Write a formal email asking about a summer course."],
};

export default function Practice() {
  const { user } = useAuth();
  const grade = Number(user?.grade) || 3;
  const profile = gradeProfile(grade);
  const [tab, setTab] = useState("free");
  const [words, setWords] = useState([]);
  const [target, setTarget] = useState("");
  const [typed, setTyped] = useState("");
  const [prompt, setPrompt] = useState(PROMPTS[grade]?.[0] || PROMPTS[5][0]);
  const [answer, setAnswer] = useState("");
  const [feedback, setFeedback] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/topics").then((r) => setWords(r.data.flatMap((x) => x.words || []).filter(Boolean)));
  }, []);

  const pool = useMemo(() => (grade <= 3 ? [...LETTERS.slice(0, 12), ...words.slice(0, 10)]
    : words.slice(0, profile.wordsPerScreen + 6)), [grade, words, profile.wordsPerScreen]);

  useEffect(() => { if (pool.length && !target) setTarget(pool[0]); }, [pool]);

  const next = () => {
    setTyped("");
    setTarget(pool[Math.floor(Math.random() * pool.length)] || "");
  };

  const check = (dictation) => {
    const ok = typed.trim().toLowerCase() === String(target).toLowerCase();
    if (ok) {
      sfx.correct?.(); toast.success("✅ صحيح!");
      api.post("/games/result", { game: dictation ? "writing_listen" : "writing_read", score: 5 })
        .catch(() => {});
      next();
    } else { sfx.wrong?.(); toast.error(`❌ الصحيح: ${target}`); }
  };

  const submitFree = async (e) => {
    e.preventDefault();
    if (answer.trim().length < 3) return toast.error("اكتب إجابتك أولاً");
    setBusy(true);
    try {
      const { data } = await api.post("/ai/writing/check", { instructions: prompt, answer });
      setFeedback(data);
      api.post("/games/result", { game: "writing_free", score: 8 }).catch(() => {});
    } catch (err) {
      toast.error(err?.response?.data?.detail || "تعذّر التصحيح");
    } finally { setBusy(false); }
  };

  const tabs = [["free", "اكتب بحرية", PenLine], ["read", "اكتب ما تقرأ", Eye],
    ["listen", "اكتب ما تسمع", Volume2]];

  return (
    <StudentShell>
      <h1 className="text-4xl font-black text-white glow-text">تدريب الكتابة</h1>
      <p className="text-white/70 mt-2 text-sm">{profile.label.ar} · الصف {grade} — التمارين حسب مستوى صفك</p>

      <div className="flex gap-2 mt-6 flex-wrap">
        {tabs.map(([k, l, Icon]) => (
          <button key={k} data-testid={`practice-tab-${k}`} onClick={() => setTab(k)}
            className="play-btn text-white text-sm flex items-center gap-2"
            style={{ background: tab === k ? "var(--play-primary)" : "rgba(255,255,255,.12)" }}>
            <Icon size={15} />{l}</button>
        ))}
      </div>

      {tab === "free" && (
        <div className="play-card mt-8 max-w-2xl" data-testid="writing-free">
          <div className="flex flex-wrap gap-2">
            {(PROMPTS[grade] || PROMPTS[5]).map((p, i) => (
              <button key={i} data-testid={`prompt-${i}`} onClick={() => { setPrompt(p); setFeedback(null); }}
                className="rounded-xl border-2 px-3 py-2 text-xs font-bold text-start"
                style={{ borderColor: prompt === p ? "var(--play-accent)" : "rgba(255,255,255,.15)" }}
                dir="ltr">{p}</button>
            ))}
          </div>
          <form onSubmit={submitFree} className="mt-5">
            <div className="text-lg font-black" dir="ltr" data-testid="writing-prompt">{prompt}</div>
            <textarea className="input mt-4" dir="ltr" rows={grade <= 4 ? 3 : 7} value={answer}
              data-testid="writing-answer" onChange={(e) => setAnswer(e.target.value)}
              placeholder="Write your answer in English..." />
            <button disabled={busy} data-testid="writing-submit-btn"
              className="play-btn text-white w-full mt-4 disabled:opacity-50"
              style={{ background: "var(--play-secondary)" }}>
              {busy ? "جارٍ التصحيح..." : "صحّح كتابتي"}</button>
          </form>
          {feedback && (
            <div className="glass-soft p-4 mt-5 space-y-2" data-testid="writing-feedback">
              <div className="font-black flex items-center gap-2">
                <Sparkles size={16} style={{ color: "#ffb43f" }} />النتيجة: {feedback.score}/100</div>
              {feedback.feedback_ar && <p className="text-sm text-white/80 leading-loose">{feedback.feedback_ar}</p>}
              {!!(feedback.corrections || []).length && (
                <ul className="text-sm space-y-1" dir="ltr">
                  {feedback.corrections.map((c, i) => <li key={i}>✏️ {c}</li>)}
                </ul>
              )}
              {!!(feedback.strengths || []).length && (
                <ul className="text-sm space-y-1" dir="ltr">
                  {feedback.strengths.map((c, i) => <li key={i}>💪 {c}</li>)}
                </ul>
              )}
            </div>
          )}
        </div>
      )}

      {tab === "read" && (
        <div className="play-card mt-8 max-w-xl" data-testid="writing-read">
          <p className="text-sm text-white/70">انظر إلى {grade <= 3 ? "الحرف" : "الكلمة"} ثم اكتبها:</p>
          <div className="mt-4 rounded-2xl bg-white/10 border-2 border-dashed border-white/15 p-8 text-center">
            <div className="text-4xl font-black" dir="ltr" data-testid="read-target">{target}</div>
          </div>
          <input className="input mt-4 text-center text-lg" dir="ltr" value={typed}
            data-testid="read-input" onChange={(e) => setTyped(e.target.value)} placeholder="type here" />
          <div className="flex gap-2 mt-4">
            <button onClick={() => check(false)} data-testid="read-check-btn"
              className="play-btn text-white flex-1" style={{ background: "var(--play-secondary)" }}>تحقق</button>
            <button onClick={next} data-testid="read-next-btn"
              className="pro-btn bg-white/12 text-white border border-white/20">التالي</button>
          </div>
        </div>
      )}

      {tab === "listen" && (
        <div className="play-card mt-8 max-w-xl" data-testid="writing-listen">
          <p className="text-sm text-white/70">
            اسمع {grade <= 3 ? "الحرف" : "الكلمة"} ثم اكتبها (بدون النظر):</p>
          <button onClick={() => say(target)} data-testid="listen-play-btn"
            className="play-btn text-white w-full mt-4 flex items-center justify-center gap-2"
            style={{ background: "var(--play-accent)" }}><Volume2 size={18} />اسمع مرة أخرى</button>
          <input className="input mt-4 text-center text-lg" dir="ltr" value={typed}
            data-testid="listen-input" onChange={(e) => setTyped(e.target.value)} placeholder="type what you hear" />
          <div className="flex gap-2 mt-4">
            <button onClick={() => check(true)} data-testid="listen-check-btn"
              className="play-btn text-white flex-1" style={{ background: "var(--play-secondary)" }}>تحقق</button>
            <button onClick={next} data-testid="listen-next-btn"
              className="pro-btn bg-white/12 text-white border border-white/20">التالي</button>
          </div>
        </div>
      )}
    </StudentShell>
  );
}
